import {
  FindCategoriesFilterOptionsOutput,
  FindClassificationsFilterOptionsOutput,
  FindCollectionsFilterOptionsOutput,
  FindColorsFilterOptionsOutput,
  FindDeliveryWindowsFilterOptionsOutput,
  FindReferenceColorsFilterOptionsOutput,
  FindSizesFilterOptionsOutput,
} from '../../../services/modules/filterOptions/filterOptions.types';
import onlineFilterOptionsRepository from '../../../services/modules/filterOptions/repositories/onlineFilterOptions.repository';
import { offlineCatalogsRepository } from '../repositories/offlineCatalogs.repository';
import { offlineCategoriesRepository } from '../repositories/offlineCategories.repository';
import { offlineClassificationsRepository } from '../repositories/offlineClassifications.repository';
import { offlineCollectionsRepository } from '../repositories/offlineCollections.repository';
import { offlineColorsRepository } from '../repositories/offlineColors.repository';
import { offlineDeliveryWindowsRepository } from '../repositories/offlineDeliveryWindows.repository';
import { offlineReferenceColorsRepository } from '../repositories/offlineReferenceColors.repository';
import { offlineSizesRepository } from '../repositories/offlineSizes.repository';
import { OfflineCategoryTransformers } from '../transformers/OfflineDatabaseCategory.transformers';
import { OfflineClassificationTransformers } from '../transformers/OfflineDatabaseClassification.transformers';
import { OfflineCollectionTransformers } from '../transformers/OfflineDatabaseCollection.transformers';
import { OfflineColorTransformers } from '../transformers/OfflineDatabaseColor.transformers';
import { OfflineDeliveryWindowTransformers } from '../transformers/OfflineDatabaseDeliveryWindow.transformers';
import { OfflineReferenceColorTransformers } from '../transformers/OfflineDatabaseReferenceColor.transformers';
import { OfflineSizeTransformers } from '../transformers/OfflineDatabaseSize.transformers';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import { hasOfflineParams } from './utils/onlineFilterOptions.repository.offlineAdapters.utils';
import {
  filterNotAvailableInOfflineError,
  notImplementedInOfflineError,
} from './utils/repository.offlineAdapters.utils';

const onlineFilterOptionsRepositoryOfflineAdapters = (): ReturnType<
  typeof onlineFilterOptionsRepository
> => {
  return {
    findStoreProductsFilterOptions: () =>
      notImplementedInOfflineError('findStoreProductsFilterOptions'),
    findStoreColorsFilterOptions: () =>
      notImplementedInOfflineError('findStoreColorsFilterOptions'),
    path: onlineFilterOptionsRepository().path,
    findAdminTeamsFilterOptions: () => notImplementedInOfflineError('findAdminTeamsFilterOptions'),
    findCitiesFilterOptions: () => notImplementedInOfflineError('findCitiesFilterOptions'),
    findCustomersFilterOptions: () => notImplementedInOfflineError('findCustomersFilterOptions'),
    findPriceTablesFilterOptions: () =>
      notImplementedInOfflineError('findPriceTablesFilterOptions'),
    findStatesFilterOptions: () => notImplementedInOfflineError('findStatesFilterOptions'),
    findCustomerGroupsFilterOptions: () =>
      notImplementedInOfflineError('findCustomerGroupsFilterOptions'),
    findSalesRepresentativesFilterOptions: () =>
      notImplementedInOfflineError('findSalesRepresentativesFilterOptions'),
    findOrdersFilterOptions: () => notImplementedInOfflineError('findOrdersFilterOptions'),
    findParentOrdersFilterOptions: () =>
      notImplementedInOfflineError('findParentOrdersFilterOptions'),
    findSellersFilterOptions: () => notImplementedInOfflineError('findSellersFilterOptions'),
    findProductsFilterOptions: () => notImplementedInOfflineError('findProductsFilterOptions'),
    findKitsFilterOptions: () => notImplementedInOfflineError('findKitsFilterOptions'),
    findLocalsFilterOptions: () => notImplementedInOfflineError('findLocalsFilterOptions'),
    findLocalsStatesFilterOptions: () =>
      notImplementedInOfflineError('findLocalsStatesFilterOptions'),
    findSalesOpportunityFilterOptions: () =>
      notImplementedInOfflineError('findSalesOpportunityFilterOptions'),
    findCaseFilterOptions: () => notImplementedInOfflineError('findCaseFilterOptions'),
    findCaseStatusFilterOptions: () => notImplementedInOfflineError('findCaseStatusFilterOptions'),
    findCaseAuthorsFilterOptions: () =>
      notImplementedInOfflineError('findCaseAuthorsFilterOptions'),
    findCaseResponsiblesFilterOptions: () =>
      notImplementedInOfflineError('findCaseResponsiblesFilterOptions'),
    findConsultantsFilterOptions: () =>
      notImplementedInOfflineError('findConsultantsFilterOptions'),
    findCatalogsFilterOptions: () => notImplementedInOfflineError('findCatalogsFilterOptions'),
    findInvoicesFilterOptions: () => notImplementedInOfflineError('findInvoicesFilterOptions'),
    findSuggestedCopyOrdersFilterOptions: () =>
      notImplementedInOfflineError('findSuggestedCopyOrdersFilterOptions'),
    findOrderVouchersFilterOptions: () =>
      notImplementedInOfflineError('findOrderVouchersFilterOptions'),
    findSalesTargetsFilterOptions: () =>
      notImplementedInOfflineError('findSalesTargetsFilterOptions'),
    findAccountsReceivableFilterOptions: () =>
      notImplementedInOfflineError('findAccountsReceivableFilterOptions'),
    findBrandsFilterOptions: () => notImplementedInOfflineError('findBrandsFilterOptions'),
    findClassificationsNoRbacFilterOptions: () =>
      notImplementedInOfflineError('findClassificationsNoRbacFilterOptions'),
    findSkuCodesFilterOptions: () => notImplementedInOfflineError('findSkuCodesFilterOptions'),
    findPromotionCodesFilterOptions: () =>
      notImplementedInOfflineError('findPromotionCodesFilterOptions'),
    findCatalogAccessClassifications: () =>
      notImplementedInOfflineError('findCatalogAccessClassifications'),

    findReferenceColorsFilterOptions: async ({ skip, limit, basedOnList, catalogId, name }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindSizesFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineReferenceColorsRepository.pageByIds({
        skip,
        limit,
        query: name,
        orderBy: 'name',
        ids: catalog.filterOptionsMetadata.referenceColorIds,
      });

      return createApiSauceResponse<FindReferenceColorsFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineReferenceColorTransformers.toFilterOptionsReferenceColor),
        },
      });
    },

    findColorsFilterOptions: async ({ skip, limit, basedOnList, catalogId, query }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindColorsFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineColorsRepository.pageByIds({
        skip,
        limit,
        query,
        orderBy: 'code',
        ids: catalog.filterOptionsMetadata.colorIds,
      });

      return createApiSauceResponse<FindColorsFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineColorTransformers.toFilterOptionsColor),
        },
      });
    },

    findSizesFilterOptions: async ({ limit, skip, basedOnList, catalogId, name }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindSizesFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineSizesRepository.pageByIds({
        skip,
        limit,
        query: name,
        orderBy: 'position',
        ids: catalog.filterOptionsMetadata.sizeIds,
      });

      return createApiSauceResponse<FindSizesFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineSizeTransformers.toFilterOptionsSize),
        },
      });
    },

    findCategoriesFilterOptions: async ({ skip, limit, basedOnList, catalogId, nameOrCode }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindCategoriesFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineCategoriesRepository.pageByIds({
        skip,
        limit,
        orderBy: 'name',
        query: nameOrCode,
        ids: catalog.filterOptionsMetadata.categoryIds,
      });

      return createApiSauceResponse<FindCategoriesFilterOptionsOutput>({
        ok: true,
        data: {
          count: 1,
          data: data.data.map(OfflineCategoryTransformers.toFilterOptionsCategory),
        },
      });
    },

    findCollectionsFilterOptions: async ({ skip, limit, basedOnList, catalogId, query }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindCollectionsFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineCollectionsRepository.pageByIds({
        skip,
        limit,
        query,
        orderBy: 'code',
        ids: catalog.filterOptionsMetadata.collectionIds,
      });

      return createApiSauceResponse<FindCollectionsFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineCollectionTransformers.toFilterOptionsCollection),
        },
      });
    },

    findDeliveryWindowsFilterOptions: async ({ skip, limit, basedOnList, catalogId, name }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindDeliveryWindowsFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineDeliveryWindowsRepository.pageByIds({
        skip,
        limit,
        query: name,
        orderBy: 'code',
        ids: catalog.filterOptionsMetadata.deliveryWindowIds,
      });

      return createApiSauceResponse<FindDeliveryWindowsFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineDeliveryWindowTransformers.toFilterOptionsDeliveryWindow),
        },
      });
    },

    findClassificationsFilterOptions: async ({
      skip,
      limit,
      query,
      catalogId,
      basedOnList,
      classificationTypeId,
    }) => {
      if (!hasOfflineParams(catalogId, basedOnList)) {
        return filterNotAvailableInOfflineError();
      }

      const catalog = await offlineCatalogsRepository.getById(catalogId);
      if (!catalog) {
        return createApiSauceResponse<FindClassificationsFilterOptionsOutput>({ ok: false });
      }

      const data = await offlineClassificationsRepository.pageByIdsAndClassificationTypeId({
        skip,
        limit,
        query,
        orderBy: 'code',
        classificationTypeId,
        ids: catalog.filterOptionsMetadata.classificationIds,
      });

      return createApiSauceResponse<FindClassificationsFilterOptionsOutput>({
        ok: true,
        data: {
          count: data.count,
          data: data.data.map(OfflineClassificationTransformers.toFilterOptionsClassification),
        },
      });
    },
  };
};

export default onlineFilterOptionsRepositoryOfflineAdapters;
